import type { Icon } from '../../design-system/icons';
import { Skeleton } from '../ui/skeleton';
import { HintTrigger } from './HintTrigger';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export interface KpiCardProps {
  /** Duotone Phosphor glyph shown in the stone-100 round box. */
  icon: Icon;
  label: string;
  value: string | number;
  valueTooltip?: string;
  /** A short caption under the value, e.g. why it reads "—". */
  hint?: string;
  /** Explains the hint: shown on hover, focus and tap, and read by screen readers. */
  hintDetail?: string;
  labelAddon?: React.ReactNode;
  description?: string;
  loading?: boolean;
  compact?: boolean;
}

/**
 * Shared campaign KPI stat card (Figma 1672:33334): a stone-100 round icon box
 * on the left, label + big value on the right. No delta, no progress bar.
 *
 * Single source of truth for the reporting "General Insights" strip and the
 * Creators / Finalized roster KPI strips (via RosterKpiCard).
 */
export function KpiCard({
  icon: IconCmp,
  label,
  value,
  valueTooltip,
  hint,
  hintDetail,
  labelAddon,
  description,
  loading = false,
  compact = false,
}: KpiCardProps) {
  return (
    <div
      // Stable hook for tests that assert a surface has NO KPI strip — notably
      // the public (no-login) share shell, which must never show campaign-wide
      // aggregates the link didn't grant.
      data-testid="kpi-card"
      // Below 14rem the full-size icon leaves too little room for a 7-character
      // value (₹20.00L, ₹1.20Cr), so the card goes compact on its own width.
      className="@container/kpi h-full"
    >
      <div
        className={cn(
          'border-border bg-card flex h-full items-center gap-3 rounded-xl border px-4 py-[18px] @max-[14rem]/kpi:gap-2 @max-[14rem]/kpi:px-3',
          compact && 'gap-2 px-3',
        )}
      >
        <div
          className={cn(
            'flex size-12 shrink-0 items-center justify-center rounded-full bg-stone-100 @max-[14rem]/kpi:size-10',
            compact && 'size-10',
          )}
        >
          <IconCmp size={compact ? 24 : 30} weight="duotone" className="text-foreground @max-[14rem]/kpi:size-6" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {labelAddon ? (
            <div className="flex min-w-0 items-center gap-1">
              <p className="text-body-sm-regular text-foreground min-w-0 wrap-break-word">{label}</p>
              {labelAddon}
            </div>
          ) : (
            <p className="text-body-sm-regular text-foreground wrap-break-word">{label}</p>
          )}
          {loading ? (
            <Skeleton className="h-7 w-20" />
          ) : valueTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <p
                  tabIndex={0}
                  className={cn(
                    'text-heading-h4/9 text-foreground focus-visible:ring-ring truncate rounded-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset @max-[14rem]/kpi:text-heading-h5/8',
                    compact && 'text-heading-h5/8',
                  )}
                >
                  {value}
                </p>
              </TooltipTrigger>
              <TooltipContent data-clarity-mask="True">{valueTooltip}</TooltipContent>
            </Tooltip>
          ) : (
            <p
              className={cn(
                'text-heading-h4/9 text-foreground truncate font-medium @max-[14rem]/kpi:text-heading-h5/8',
                compact && 'text-heading-h5/8',
              )}
            >
              {value}
            </p>
          )}
          {hint && (
            <p className="text-caption-regular text-muted-foreground">
              {hintDetail ? <HintTrigger hint={hintDetail}>{hint}</HintTrigger> : hint}
            </p>
          )}
          {description ? (
            <p className="text-caption-regular text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
